/**
 * Inter-warehouse Stock Transfers
 *
 * PageHeader + KPI strip + filter bar + DataTable + detail/create/receive sheets.
 */
import { useMemo, useState, type ReactElement } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { ArrowRight, Check, Clock, Eye, Package, Plus, Search, Truck, X } from "lucide-react";
import { formatMoney, formatMoneySmart } from "@bizop/shared/currency";
import {
  Button, DataTable, Input, KpiCard, Label,
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
  Sheet, SheetBody, SheetContent, SheetDescription, SheetFooter,
  SheetHeader, SheetTitle, StatusPill,
  type StatusPillVariant, cn, toast, type DataTableColumn,
} from "@bizop/ui-kit";
import { PageHeader } from "../../../components/PageHeader";
import { ExportButton } from "../../../components/export";
import { downloadCsv, exportFilename, formatDateForCsv, toCsv, type ExportColumn } from "../../../lib/export";
import { mockWarehouses, mockProducts, type MockWarehouse, type MockProduct } from "../../../lib/inventoryMock";
import { useMockData } from "../../../lib/mockMode";
import { usePersistentMockState } from "../../../lib/usePersistentMockState";

export const Route = createFileRoute("/_app/inventory/transfers")({
  component: StockTransfersPage,
});

/* ── Types ────────────────────────────────────────────────── */

type TransferStatus = "draft" | "pending" | "in_transit" | "received" | "cancelled";

type TransferItem = {
  productId: string;
  productName: string;
  qty: number;
  qtyReceived: number;
  unitCostCents: bigint;
};

type MockStockTransfer = {
  _id: string;
  transferNumber: string;
  fromWarehouseId: string;
  toWarehouseId: string;
  status: TransferStatus;
  items: TransferItem[];
  totalItems: number;
  totalValueCents: bigint;
  requestedBy: string;
  approvedBy: string | null;
  shippedAt: number | null;
  receivedAt: number | null;
  notes: string;
  createdAt: number;
  updatedAt: number;
};

/* ── Constants ────────────────────────────────────────────── */

const NOW = new Date("2026-05-09T08:00:00Z").getTime();
const DAY = 24 * 60 * 60 * 1000;

const STATUS_VARIANTS: Record<TransferStatus, StatusPillVariant> = {
  draft: "neutral", pending: "warning", in_transit: "info", received: "success", cancelled: "error",
};
const STATUS_LABELS: Record<TransferStatus, string> = {
  draft: "Draft", pending: "Pending", in_transit: "In Transit", received: "Received", cancelled: "Cancelled",
};

function formatDate(ms: number): string {
  return new Date(ms).toLocaleDateString("en-BD", { year: "numeric", month: "short", day: "2-digit" });
}

/* ── Mock data ────────────────────────────────────────────── */

const WH = { raw1: "wh_dhk_raw", raw2: "wh_ctg_raw", fg1: "wh_dhk_fg", fg2: "wh_ctg_fg" };

const mockTransfers = ([
  { _id: "trf_001", transferNumber: "TRF-0001", fromWarehouseId: WH.raw1, toWarehouseId: WH.raw2, status: "received",
    items: [
      { productId: "p_steel_coil", productName: "Steel Coil HR", qty: 50, qtyReceived: 48, unitCostCents: 120000n },
      { productId: "p_zinc_ingot", productName: "Zinc Ingot", qty: 200, qtyReceived: 200, unitCostCents: 8500n },
    ],
    totalItems: 250, totalValueCents: 7700000n, requestedBy: "Rafiq Ahmed", approvedBy: "Kamal Hossain",
    shippedAt: NOW - 12 * DAY, receivedAt: NOW - 10 * DAY, notes: "Urgent restock for Chittagong plant.", createdAt: NOW - 14 * DAY },
  { _id: "trf_002", transferNumber: "TRF-0002", fromWarehouseId: WH.fg1, toWarehouseId: WH.fg2, status: "in_transit",
    items: [{ productId: "p_roofing_sheet", productName: "Roofing Sheet 0.47mm", qty: 500, qtyReceived: 0, unitCostCents: 45000n }],
    totalItems: 500, totalValueCents: 22500000n, requestedBy: "Salma Begum", approvedBy: "Kamal Hossain",
    shippedAt: NOW - 2 * DAY, receivedAt: null, notes: "Dealer demand surge in Chittagong region.", createdAt: NOW - 5 * DAY },
  { _id: "trf_003", transferNumber: "TRF-0003", fromWarehouseId: WH.raw2, toWarehouseId: WH.raw1, status: "pending",
    items: [
      { productId: "p_paint_primer", productName: "Primer Coating 20L", qty: 80, qtyReceived: 0, unitCostCents: 15000n },
      { productId: "p_zinc_ingot", productName: "Zinc Ingot", qty: 100, qtyReceived: 0, unitCostCents: 8500n },
    ],
    totalItems: 180, totalValueCents: 2050000n, requestedBy: "Nasir Uddin", approvedBy: null,
    shippedAt: null, receivedAt: null, notes: "Balancing raw material across plants.", createdAt: NOW - 3 * DAY },
  { _id: "trf_004", transferNumber: "TRF-0004", fromWarehouseId: WH.fg2, toWarehouseId: WH.fg1, status: "draft",
    items: [{ productId: "p_roofing_sheet", productName: "Roofing Sheet 0.47mm", qty: 150, qtyReceived: 0, unitCostCents: 45000n }],
    totalItems: 150, totalValueCents: 6750000n, requestedBy: "Salma Begum", approvedBy: null,
    shippedAt: null, receivedAt: null, notes: "", createdAt: NOW - 1 * DAY },
  { _id: "trf_005", transferNumber: "TRF-0005", fromWarehouseId: WH.raw1, toWarehouseId: WH.fg1, status: "received",
    items: [{ productId: "p_steel_coil", productName: "Steel Coil HR", qty: 100, qtyReceived: 100, unitCostCents: 120000n }],
    totalItems: 100, totalValueCents: 12000000n, requestedBy: "Rafiq Ahmed", approvedBy: "Kamal Hossain",
    shippedAt: NOW - 20 * DAY, receivedAt: NOW - 18 * DAY, notes: "Monthly production feed.", createdAt: NOW - 22 * DAY },
  { _id: "trf_006", transferNumber: "TRF-0006", fromWarehouseId: WH.fg1, toWarehouseId: WH.fg2, status: "cancelled",
    items: [{ productId: "p_paint_primer", productName: "Primer Coating 20L", qty: 40, qtyReceived: 0, unitCostCents: 15000n }],
    totalItems: 40, totalValueCents: 600000n, requestedBy: "Nasir Uddin", approvedBy: null,
    shippedAt: null, receivedAt: null, notes: "Cancelled -- product recalled.", createdAt: NOW - 8 * DAY },
  { _id: "trf_007", transferNumber: "TRF-0007", fromWarehouseId: WH.raw2, toWarehouseId: WH.fg2, status: "pending",
    items: [
      { productId: "p_steel_coil", productName: "Steel Coil HR", qty: 75, qtyReceived: 0, unitCostCents: 120000n },
      { productId: "p_paint_primer", productName: "Primer Coating 20L", qty: 30, qtyReceived: 0, unitCostCents: 15000n },
    ],
    totalItems: 105, totalValueCents: 9450000n, requestedBy: "Rafiq Ahmed", approvedBy: null,
    shippedAt: null, receivedAt: null, notes: "Awaiting QC clearance.", createdAt: NOW - 2 * DAY },
  { _id: "trf_008", transferNumber: "TRF-0008", fromWarehouseId: WH.fg1, toWarehouseId: WH.raw1, status: "received",
    items: [{ productId: "p_zinc_ingot", productName: "Zinc Ingot", qty: 300, qtyReceived: 295, unitCostCents: 8500n }],
    totalItems: 300, totalValueCents: 2550000n, requestedBy: "Salma Begum", approvedBy: "Kamal Hossain",
    shippedAt: NOW - 6 * DAY, receivedAt: NOW - 4 * DAY, notes: "5 units short on receipt -- damaged in transit.", createdAt: NOW - 9 * DAY },
] as Omit<MockStockTransfer, "updatedAt">[]).map((t) => ({ ...t, updatedAt: t.createdAt })) as MockStockTransfer[];

/* ── Page component ───────────────────────────────────────── */

function StockTransfersPage(): ReactElement {
  const isMock = useMockData();
  void isMock;
  const [transfers, setTransfers] = usePersistentMockState<MockStockTransfer[]>("stockTransfers", mockTransfers);
  const [warehouses] = usePersistentMockState("warehouses", mockWarehouses);
  const [products] = usePersistentMockState("products", mockProducts);

  const warehouseById = useMemo(() => new Map(warehouses.map((w: MockWarehouse) => [w._id, w])), [warehouses]);
  /* Filters */
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [warehouseFilter, setWarehouseFilter] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");

  const filteredTransfers = useMemo(() => {
    let result = transfers;
    if (statusFilter !== "all") result = result.filter((t: MockStockTransfer) => t.status === statusFilter);
    if (warehouseFilter !== "all")
      result = result.filter((t: MockStockTransfer) => t.fromWarehouseId === warehouseFilter || t.toWarehouseId === warehouseFilter);
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      result = result.filter((t: MockStockTransfer) => t.transferNumber.toLowerCase().includes(q));
    }
    return result.slice().sort((a: MockStockTransfer, b: MockStockTransfer) => b.createdAt - a.createdAt);
  }, [transfers, statusFilter, warehouseFilter, searchQuery]);

  /* KPIs */
  const kpis = useMemo(() => {
    const monthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1).getTime();
    let pending = 0, inTransit = 0, completedThisMonth = 0, totalValueMoved = 0n;
    for (const t of transfers) {
      if (t.status === "pending") pending++;
      if (t.status === "in_transit") inTransit++;
      if (t.status === "received") {
        totalValueMoved += t.totalValueCents;
        if (t.receivedAt && t.receivedAt >= monthStart) completedThisMonth++;
      }
    }
    return { pending, inTransit, completedThisMonth, totalValueMoved };
  }, [transfers]);

  /* Sheet state */
  const [createOpen, setCreateOpen] = useState(false);
  const [detailRow, setDetailRow] = useState<MockStockTransfer | null>(null);
  const [receiveRow, setReceiveRow] = useState<MockStockTransfer | null>(null);

  /* Table columns */
  const columns = useMemo<DataTableColumn<MockStockTransfer>[]>(() => [
    { accessorKey: "transferNumber", header: "Transfer #", meta: { mono: true },
      cell: ({ row }: { row: { original: MockStockTransfer } }) => (
        <span className="font-mono text-[12px]">{row.original.transferNumber}</span>) },
    { id: "fromWarehouse", header: "From",
      cell: ({ row }: { row: { original: MockStockTransfer } }) => (
        <span className="text-(--text-primary)">{warehouseById.get(row.original.fromWarehouseId)?.displayName ?? "---"}</span>) },
    { id: "toWarehouse", header: "To",
      cell: ({ row }: { row: { original: MockStockTransfer } }) => (
        <span className="text-(--text-primary)">{warehouseById.get(row.original.toWarehouseId)?.displayName ?? "---"}</span>) },
    { id: "items", header: "Items", meta: { hideOnMobile: true },
      cell: ({ row }: { row: { original: MockStockTransfer } }) => (
        <span className="tabular-nums">{row.original.items.length} SKU / {row.original.totalItems} units</span>) },
    { id: "value", header: "Value", meta: { numeric: true, mono: true },
      cell: ({ row }: { row: { original: MockStockTransfer } }) => (
        <span className="font-mono tabular-nums">{formatMoney(row.original.totalValueCents, "BDT", { locale: "en-BD" })}</span>) },
    { accessorKey: "status", header: "Status",
      cell: ({ row }: { row: { original: MockStockTransfer } }) => (
        <StatusPill variant={STATUS_VARIANTS[row.original.status]}>{STATUS_LABELS[row.original.status]}</StatusPill>) },
    { accessorKey: "createdAt", header: "Date", meta: { hideOnMobile: true },
      cell: ({ row }: { row: { original: MockStockTransfer } }) => (
        <span className="text-(--text-secondary) tabular-nums">{formatDate(row.original.createdAt)}</span>) },
    { id: "actions", header: "", meta: { hideOnMobile: true },
      cell: ({ row }: { row: { original: MockStockTransfer } }) => (
        <Button variant="ghost" size="sm" onClick={(e: React.MouseEvent) => { e.stopPropagation(); setDetailRow(row.original); }}>
          <Eye className="size-4" />
        </Button>) },
  ], [warehouseById]);

  /* Actions */
  function handleApprove(t: MockStockTransfer) {
    setTransfers((prev: MockStockTransfer[]) => prev.map((r: MockStockTransfer) =>
      r._id === t._id ? { ...r, status: "pending" as const, approvedBy: "Current User" } : r));
    toast.success(`Transfer ${t.transferNumber} approved`);
  }
  function handleShip(t: MockStockTransfer) {
    setTransfers((prev: MockStockTransfer[]) => prev.map((r: MockStockTransfer) =>
      r._id === t._id ? { ...r, status: "in_transit" as const, shippedAt: Date.now() } : r));
    toast.success(`Transfer ${t.transferNumber} shipped`);
  }
  function handleCancel(t: MockStockTransfer) {
    setTransfers((prev: MockStockTransfer[]) => prev.map((r: MockStockTransfer) =>
      r._id === t._id ? { ...r, status: "cancelled" as const } : r));
    setDetailRow(null);
    toast.success(`Transfer ${t.transferNumber} cancelled`);
  }
  function handleReceive(t: MockStockTransfer, receivedItems: TransferItem[]) {
    setTransfers((prev: MockStockTransfer[]) => prev.map((r: MockStockTransfer) =>
      r._id === t._id ? { ...r, status: "received" as const, receivedAt: Date.now(), items: receivedItems } : r));
    setReceiveRow(null);
    setDetailRow(null);
    toast.success(`Transfer ${t.transferNumber} received`);
  }
  function handleCreate(values: { fromWarehouseId: string; toWarehouseId: string; items: TransferItem[]; notes: string }) {
    const totalItems = values.items.reduce((s: number, it: TransferItem) => s + it.qty, 0);
    const totalValueCents = values.items.reduce((s: bigint, it: TransferItem) => s + it.unitCostCents * BigInt(it.qty), 0n);
    const nextNum = transfers.length + 1;
    const row: MockStockTransfer = {
      _id: crypto.randomUUID(), transferNumber: `TRF-${String(nextNum).padStart(4, "0")}`,
      fromWarehouseId: values.fromWarehouseId, toWarehouseId: values.toWarehouseId, status: "draft",
      items: values.items, totalItems, totalValueCents, requestedBy: "Current User", approvedBy: null,
      shippedAt: null, receivedAt: null, notes: values.notes, createdAt: Date.now(), updatedAt: Date.now(),
    };
    setTransfers((prev: MockStockTransfer[]) => [row, ...prev]);
    setCreateOpen(false);
    toast.success(`Transfer ${row.transferNumber} created`);
  }

  /* CSV export */
  function handleExportCsv(extension: "csv" | "xls") {
    type TrfRow = { transferNumber: string; fromWarehouse: string; toWarehouse: string; itemCount: number;
      totalItems: number; totalValueCents: bigint; status: string; requestedBy: string; approvedBy: string;
      shippedAt: string; receivedAt: string; notes: string; createdAt: string };
    const rows: TrfRow[] = filteredTransfers.map((t: MockStockTransfer) => ({
      transferNumber: t.transferNumber,
      fromWarehouse: warehouseById.get(t.fromWarehouseId)?.displayName ?? t.fromWarehouseId,
      toWarehouse: warehouseById.get(t.toWarehouseId)?.displayName ?? t.toWarehouseId,
      itemCount: t.items.length, totalItems: t.totalItems, totalValueCents: t.totalValueCents,
      status: STATUS_LABELS[t.status], requestedBy: t.requestedBy, approvedBy: t.approvedBy ?? "",
      shippedAt: t.shippedAt ? formatDateForCsv(t.shippedAt) : "",
      receivedAt: t.receivedAt ? formatDateForCsv(t.receivedAt) : "",
      notes: t.notes, createdAt: formatDateForCsv(t.createdAt),
    }));
    const cols: ExportColumn<TrfRow>[] = [
      { key: "transferNumber", label: "Transfer #" }, { key: "fromWarehouse", label: "From warehouse" },
      { key: "toWarehouse", label: "To warehouse" }, { key: "itemCount", label: "SKU count" },
      { key: "totalItems", label: "Total units" }, { key: "totalValueCents", label: "Total value BDT" },
      { key: "status", label: "Status" }, { key: "requestedBy", label: "Requested by" },
      { key: "approvedBy", label: "Approved by" }, { key: "shippedAt", label: "Shipped" },
      { key: "receivedAt", label: "Received" }, { key: "notes", label: "Notes" },
      { key: "createdAt", label: "Created" },
    ];
    downloadCsv(exportFilename("stock-transfers", extension), toCsv(rows, cols));
  }

  /* Render */
  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Stock Transfers"
        description="Move inventory between warehouses with approval tracking and receipt confirmation."
        actions={<>
          <ExportButton onCsv={() => handleExportCsv("csv")} onExcel={() => handleExportCsv("xls")} />
          <Button onClick={() => setCreateOpen(true)}><Plus className="size-4" /> New Transfer</Button>
        </>}
      />

      {/* KPI strip */}
      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" aria-label="Transfer KPIs">
        <KpiCard caption={<span className="inline-flex items-center gap-1.5"><Clock className="size-3" /> Pending Transfers</span>}
          value={String(kpis.pending)} hint="Awaiting approval or shipment" />
        <KpiCard caption={<span className="inline-flex items-center gap-1.5"><Truck className="size-3" /> In Transit</span>}
          value={String(kpis.inTransit)} hint="Shipped, awaiting receipt" />
        <KpiCard caption={<span className="inline-flex items-center gap-1.5"><Check className="size-3" /> Completed (this month)</span>}
          value={String(kpis.completedThisMonth)} hint="Received this calendar month" />
        <KpiCard caption={<span className="inline-flex items-center gap-1.5"><Package className="size-3" /> Total Value Moved</span>}
          value={formatMoneySmart(kpis.totalValueMoved, "BDT", { locale: "en-BD" })} hint="Lifetime received transfers" />
      </section>

      {/* Filter bar */}
      <section className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1.5">
          <Label className="text-[11px] uppercase tracking-wide text-(--text-tertiary)">Status</Label>
          <Select value={statusFilter} onValueChange={(v: string) => setStatusFilter(v)}>
            <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="in_transit">In Transit</SelectItem>
              <SelectItem value="received">Received</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-[11px] uppercase tracking-wide text-(--text-tertiary)">Warehouse</Label>
          <Select value={warehouseFilter} onValueChange={(v: string) => setWarehouseFilter(v)}>
            <SelectTrigger className="w-[200px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All warehouses</SelectItem>
              {warehouses.filter((w: MockWarehouse) => w.isActive).map((w: MockWarehouse) => (
                <SelectItem key={w._id} value={w._id}>{w.displayName}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-[11px] uppercase tracking-wide text-(--text-tertiary)">Search</Label>
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 size-4 text-(--text-tertiary)" />
            <Input placeholder="Transfer #..." value={searchQuery}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)} className="pl-9 w-[200px]" />
          </div>
        </div>
      </section>

      {/* Data table */}
      <DataTable data={filteredTransfers} columns={columns} getRowId={(r: MockStockTransfer) => r._id}
        onRowClick={(row: { original: MockStockTransfer }) => setDetailRow(row.original)}
        emptyTitle="No transfers found"
        emptyDescription="Create a new stock transfer to move inventory between warehouses."
        emptyIcon={<Truck className="h-5 w-5" aria-hidden />}
        emptyAction={<Button onClick={() => setCreateOpen(true)}><Plus className="size-4" /> New Transfer</Button>} />

      {/* Create transfer sheet */}
      <Sheet open={createOpen} onOpenChange={(open: boolean) => setCreateOpen(open)}>
        <SheetContent side="right" size="lg">
          <SheetHeader>
            <SheetTitle>New Stock Transfer</SheetTitle>
            <SheetDescription>Select source and destination warehouses, then add products to transfer.</SheetDescription>
          </SheetHeader>
          <SheetBody>
            <CreateTransferForm warehouses={warehouses.filter((w: MockWarehouse) => w.isActive)}
              products={products.filter((p: MockProduct) => p.isActive)}
              onSubmit={handleCreate} onCancel={() => setCreateOpen(false)} />
          </SheetBody>
        </SheetContent>
      </Sheet>

      {/* Detail sheet */}
      <Sheet open={detailRow !== null} onOpenChange={(open: boolean) => { if (!open) setDetailRow(null); }}>
        <SheetContent side="right" size="lg">
          {detailRow ? (
            <TransferDetail row={detailRow} warehouseById={warehouseById}
              onApprove={() => { handleApprove(detailRow); setDetailRow({ ...detailRow, status: "pending", approvedBy: "Current User" }); }}
              onShip={() => { handleShip(detailRow); setDetailRow({ ...detailRow, status: "in_transit", shippedAt: Date.now() }); }}
              onCancel={() => handleCancel(detailRow)}
              onReceive={() => setReceiveRow(detailRow)} />
          ) : null}
        </SheetContent>
      </Sheet>

      {/* Receive sheet */}
      <Sheet open={receiveRow !== null} onOpenChange={(open: boolean) => { if (!open) setReceiveRow(null); }}>
        <SheetContent side="right" size="lg">
          {receiveRow ? (
            <ReceiveTransferForm transfer={receiveRow}
              onReceive={(items: TransferItem[]) => handleReceive(receiveRow, items)}
              onCancel={() => setReceiveRow(null)} />
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  );
}

/* ── Transfer detail panel ────────────────────────────────── */

function TransferDetail({ row, warehouseById, onApprove, onShip, onCancel, onReceive }: {
  row: MockStockTransfer; warehouseById: Map<string, MockWarehouse>;
  onApprove: () => void; onShip: () => void; onCancel: () => void; onReceive: () => void;
}) {
  const fromWh = warehouseById.get(row.fromWarehouseId);
  const toWh = warehouseById.get(row.toWarehouseId);
  const hasShortage = row.status === "received" && row.items.some((it: TransferItem) => it.qtyReceived < it.qty);

  return (
    <>
      <SheetHeader>
        <SheetTitle className="flex items-center gap-3">
          <span className="font-mono">{row.transferNumber}</span>
          <StatusPill variant={STATUS_VARIANTS[row.status]}>{STATUS_LABELS[row.status]}</StatusPill>
        </SheetTitle>
        <SheetDescription>
          {fromWh?.displayName ?? "---"} <ArrowRight className="inline size-3 mx-1" /> {toWh?.displayName ?? "---"}
        </SheetDescription>
      </SheetHeader>
      <SheetBody>
        <div className="flex flex-col gap-5">
          {/* Summary */}
          <section className="grid grid-cols-2 gap-3">
            <DetailField label="From Warehouse" value={fromWh?.displayName ?? "---"} />
            <DetailField label="To Warehouse" value={toWh?.displayName ?? "---"} />
            <DetailField label="Requested By" value={row.requestedBy} />
            <DetailField label="Approved By" value={row.approvedBy ?? "---"} />
            <DetailField label="Total Value" value={formatMoney(row.totalValueCents, "BDT", { locale: "en-BD" })} />
            <DetailField label="Created" value={formatDate(row.createdAt)} />
          </section>

          {/* Items table */}
          <section>
            <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary) mb-2">Transfer Items</div>
            <div className="rounded-(--radius-md) border border-(--border-default) overflow-hidden">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="bg-(--bg-secondary) text-(--text-tertiary) text-left text-[11px] uppercase tracking-wide">
                    <th className="px-3 py-2">Product</th>
                    <th className="px-3 py-2 text-right">Qty Sent</th>
                    {row.status === "received" ? <th className="px-3 py-2 text-right">Qty Received</th> : null}
                    <th className="px-3 py-2 text-right">Unit Cost</th>
                    <th className="px-3 py-2 text-right">Line Total</th>
                  </tr>
                </thead>
                <tbody>
                  {row.items.map((item: TransferItem, idx: number) => {
                    const lineTotal = item.unitCostCents * BigInt(item.qty);
                    const shortage = row.status === "received" && item.qtyReceived < item.qty;
                    return (
                      <tr key={idx} className={cn("border-t border-(--border-default)", shortage && "bg-(--danger-soft)/30")}>
                        <td className="px-3 py-2 text-(--text-primary)">{item.productName}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{item.qty}</td>
                        {row.status === "received" ? (
                          <td className={cn("px-3 py-2 text-right tabular-nums", shortage && "text-(--danger) font-medium")}>
                            {item.qtyReceived}{shortage ? ` (-${item.qty - item.qtyReceived})` : null}
                          </td>
                        ) : null}
                        <td className="px-3 py-2 text-right font-mono tabular-nums">
                          {formatMoney(item.unitCostCents, "BDT", { locale: "en-BD" })}
                        </td>
                        <td className="px-3 py-2 text-right font-mono tabular-nums">
                          {formatMoney(lineTotal, "BDT", { locale: "en-BD" })}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {hasShortage ? (
              <div className="mt-2 flex items-center gap-1.5 text-[12px] text-(--danger)">
                <X className="size-3" /> Shortage detected on receipt
              </div>
            ) : null}
          </section>

          {/* Timeline */}
          <section>
            <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary) mb-2">Timeline</div>
            <div className="flex flex-col gap-2">
              <TimelineEntry label="Created" date={formatDate(row.createdAt)} detail={`by ${row.requestedBy}`} done />
              <TimelineEntry label="Approved" date={row.approvedBy ? "Approved" : "---"}
                detail={row.approvedBy ? `by ${row.approvedBy}` : "Awaiting approval"} done={row.approvedBy !== null} />
              <TimelineEntry label="Shipped" date={row.shippedAt ? formatDate(row.shippedAt) : "---"}
                detail={row.shippedAt ? "Dispatched from source" : "Not yet shipped"} done={row.shippedAt !== null} />
              <TimelineEntry label="Received" date={row.receivedAt ? formatDate(row.receivedAt) : "---"}
                detail={row.receivedAt ? "Confirmed at destination" : "Not yet received"} done={row.receivedAt !== null} />
            </div>
          </section>

          {/* Notes */}
          {row.notes ? (
            <section>
              <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary) mb-1">Notes</div>
              <p className="text-[13px] text-(--text-secondary) whitespace-pre-line">{row.notes}</p>
            </section>
          ) : null}
        </div>
      </SheetBody>
      <SheetFooter>
        {row.status === "draft" ? (<>
          <Button onClick={onApprove}><Check className="size-4" /> Approve</Button>
          <Button variant="destructive" onClick={onCancel}><X className="size-4" /> Cancel</Button>
        </>) : null}
        {row.status === "pending" ? (<>
          <Button onClick={onShip}><Truck className="size-4" /> Mark Shipped</Button>
          <Button variant="destructive" onClick={onCancel}><X className="size-4" /> Cancel</Button>
        </>) : null}
        {row.status === "in_transit" ? (
          <Button onClick={onReceive}><Check className="size-4" /> Receive</Button>
        ) : null}
      </SheetFooter>
    </>
  );
}

/* ── Timeline entry ───────────────────────────────────────── */

function TimelineEntry({ label, date, detail, done }: {
  label: string; date: string; detail: string; done: boolean;
}) {
  return (
    <div className="flex items-start gap-3">
      <div className={cn(
        "mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full border",
        done ? "border-(--success) bg-(--success-soft) text-(--success)"
             : "border-(--border-default) bg-(--bg-secondary) text-(--text-tertiary)",
      )}>
        {done ? <Check className="size-3" /> : <Clock className="size-3" />}
      </div>
      <div className="flex flex-col">
        <span className={cn("text-[13px] font-medium", done ? "text-(--text-primary)" : "text-(--text-tertiary)")}>{label}</span>
        <span className="text-[12px] text-(--text-secondary)">{date} -- {detail}</span>
      </div>
    </div>
  );
}

/* ── Create transfer form ─────────────────────────────────── */

function CreateTransferForm({ warehouses, products, onSubmit, onCancel }: {
  warehouses: MockWarehouse[]; products: MockProduct[];
  onSubmit: (v: { fromWarehouseId: string; toWarehouseId: string; items: TransferItem[]; notes: string }) => void;
  onCancel: () => void;
}) {
  const [fromId, setFromId] = useState("");
  const [toId, setToId] = useState("");
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState<TransferItem[]>([]);
  const [selectedProductId, setSelectedProductId] = useState("");
  const [addQty, setAddQty] = useState("");
  const [addCost, setAddCost] = useState("");

  function handleAddItem() {
    const product = products.find((p: MockProduct) => p._id === selectedProductId);
    if (!product) return;
    const qty = parseInt(addQty, 10);
    if (!qty || qty <= 0) { toast.error("Quantity must be greater than zero"); return; }
    const costBdt = parseFloat(addCost);
    if (Number.isNaN(costBdt) || costBdt < 0) { toast.error("Enter a valid unit cost"); return; }
    const unitCostCents = BigInt(Math.round(costBdt * 100));
    if (items.find((it: TransferItem) => it.productId === selectedProductId)) {
      toast.error("Product already added. Remove it first to change quantity.");
      return;
    }
    setItems((prev: TransferItem[]) => [...prev, {
      productId: product._id, productName: product.displayName, qty, qtyReceived: 0, unitCostCents,
    }]);
    setSelectedProductId(""); setAddQty(""); setAddCost("");
  }

  function handleRemoveItem(productId: string) {
    setItems((prev: TransferItem[]) => prev.filter((it: TransferItem) => it.productId !== productId));
  }

  function handleSubmit() {
    if (!fromId) { toast.error("Select a source warehouse"); return; }
    if (!toId) { toast.error("Select a destination warehouse"); return; }
    if (fromId === toId) { toast.error("Source and destination must differ"); return; }
    if (items.length === 0) { toast.error("Add at least one product"); return; }
    onSubmit({ fromWarehouseId: fromId, toWarehouseId: toId, items, notes });
  }

  const availableProducts = products.filter(
    (p: MockProduct) => !items.some((it: TransferItem) => it.productId === p._id),
  );

  return (
    <div className="flex flex-col gap-5">
      {/* Warehouse selection */}
      <div className="grid grid-cols-2 gap-4">
        <div className="flex flex-col gap-1.5">
          <Label>From Warehouse</Label>
          <Select value={fromId} onValueChange={(v: string) => setFromId(v)}>
            <SelectTrigger><SelectValue placeholder="Select source" /></SelectTrigger>
            <SelectContent>
              {warehouses.map((w: MockWarehouse) => (
                <SelectItem key={w._id} value={w._id}>{w.displayName} ({w.code})</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label>To Warehouse</Label>
          <Select value={toId} onValueChange={(v: string) => setToId(v)}>
            <SelectTrigger><SelectValue placeholder="Select destination" /></SelectTrigger>
            <SelectContent>
              {warehouses.filter((w: MockWarehouse) => w._id !== fromId).map((w: MockWarehouse) => (
                <SelectItem key={w._id} value={w._id}>{w.displayName} ({w.code})</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      {fromId && toId && fromId === toId ? (
        <div className="text-[12px] text-(--danger)">Source and destination warehouses must be different.</div>
      ) : null}

      {/* Add product row */}
      <section>
        <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary) mb-2">Add Products</div>
        <div className="flex items-end gap-2">
          <div className="flex flex-col gap-1.5 flex-1">
            <Label className="text-[11px]">Product</Label>
            <Select value={selectedProductId} onValueChange={(v: string) => setSelectedProductId(v)}>
              <SelectTrigger><SelectValue placeholder="Select product" /></SelectTrigger>
              <SelectContent>
                {availableProducts.map((p: MockProduct) => (
                  <SelectItem key={p._id} value={p._id}>{p.displayName} ({p.sku})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1.5 w-[100px]">
            <Label className="text-[11px]">Qty</Label>
            <Input type="number" min="1" placeholder="0" value={addQty}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAddQty(e.target.value)} />
          </div>
          <div className="flex flex-col gap-1.5 w-[120px]">
            <Label className="text-[11px]">Unit Cost (BDT)</Label>
            <Input type="number" min="0" step="0.01" placeholder="0.00" value={addCost}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAddCost(e.target.value)} />
          </div>
          <Button size="sm" onClick={handleAddItem} disabled={!selectedProductId || !addQty}>
            <Plus className="size-4" />
          </Button>
        </div>
      </section>

      {/* Items list */}
      {items.length > 0 ? (
        <section>
          <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary) mb-2">Items ({items.length})</div>
          <div className="rounded-(--radius-md) border border-(--border-default) overflow-hidden">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="bg-(--bg-secondary) text-(--text-tertiary) text-left text-[11px] uppercase tracking-wide">
                  <th className="px-3 py-2">Product</th>
                  <th className="px-3 py-2 text-right">Qty</th>
                  <th className="px-3 py-2 text-right">Unit Cost</th>
                  <th className="px-3 py-2 text-right">Line Total</th>
                  <th className="px-3 py-2 w-10" />
                </tr>
              </thead>
              <tbody>
                {items.map((item: TransferItem) => {
                  const lineTotal = item.unitCostCents * BigInt(item.qty);
                  return (
                    <tr key={item.productId} className="border-t border-(--border-default)">
                      <td className="px-3 py-2 text-(--text-primary)">{item.productName}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{item.qty}</td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums">
                        {formatMoney(item.unitCostCents, "BDT", { locale: "en-BD" })}
                      </td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums">
                        {formatMoney(lineTotal, "BDT", { locale: "en-BD" })}
                      </td>
                      <td className="px-3 py-2">
                        <Button variant="ghost" size="sm" onClick={() => handleRemoveItem(item.productId)}>
                          <X className="size-3" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t border-(--border-default) bg-(--bg-secondary)">
                  <td className="px-3 py-2 font-medium" colSpan={3}>Total</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums font-medium">
                    {formatMoney(items.reduce((s: bigint, it: TransferItem) => s + it.unitCostCents * BigInt(it.qty), 0n), "BDT", { locale: "en-BD" })}
                  </td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        </section>
      ) : null}

      {/* Notes */}
      <div className="flex flex-col gap-1.5">
        <Label>Notes</Label>
        <Input placeholder="Optional transfer notes..." value={notes}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNotes(e.target.value)} />
      </div>

      {/* Actions */}
      <div className="flex items-center gap-3 pt-2">
        <Button onClick={handleSubmit} disabled={!fromId || !toId || items.length === 0}>
          <Plus className="size-4" /> Create Transfer
        </Button>
        <Button variant="ghost" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}

/* ── Receive transfer form ────────────────────────────────── */

function ReceiveTransferForm({ transfer, onReceive, onCancel }: {
  transfer: MockStockTransfer;
  onReceive: (items: TransferItem[]) => void;
  onCancel: () => void;
}) {
  const [receivedQtys, setReceivedQtys] = useState<Record<string, number>>(() => {
    const map: Record<string, number> = {};
    for (const item of transfer.items) map[item.productId] = item.qty;
    return map;
  });

  function handleQtyChange(productId: string, value: string) {
    const qty = parseInt(value, 10);
    if (Number.isNaN(qty) || qty < 0) return;
    setReceivedQtys((prev: Record<string, number>) => ({ ...prev, [productId]: qty }));
  }

  function handleSubmit() {
    const updatedItems: TransferItem[] = transfer.items.map((item: TransferItem) => ({
      ...item, qtyReceived: receivedQtys[item.productId] ?? 0,
    }));
    const hasShortage = updatedItems.some((it: TransferItem) => it.qtyReceived < it.qty);
    if (hasShortage) toast.info("Transfer received with shortages noted");
    onReceive(updatedItems);
  }

  const totalSent = transfer.items.reduce((s: number, it: TransferItem) => s + it.qty, 0);
  const totalReceived = Object.values(receivedQtys).reduce((s: number, q: number) => s + q, 0);

  return (
    <>
      <SheetHeader>
        <SheetTitle>Receive Transfer {transfer.transferNumber}</SheetTitle>
        <SheetDescription>Enter the quantity received for each item. Differences will be tracked as shortages.</SheetDescription>
      </SheetHeader>
      <SheetBody>
        <div className="flex flex-col gap-4">
          <div className="rounded-(--radius-md) border border-(--border-default) overflow-hidden">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="bg-(--bg-secondary) text-(--text-tertiary) text-left text-[11px] uppercase tracking-wide">
                  <th className="px-3 py-2">Product</th>
                  <th className="px-3 py-2 text-right">Qty Sent</th>
                  <th className="px-3 py-2 text-right">Qty Received</th>
                  <th className="px-3 py-2 text-right">Shortage</th>
                </tr>
              </thead>
              <tbody>
                {transfer.items.map((item: TransferItem) => {
                  const received = receivedQtys[item.productId] ?? 0;
                  const shortage = item.qty - received;
                  return (
                    <tr key={item.productId} className="border-t border-(--border-default)">
                      <td className="px-3 py-2 text-(--text-primary)">{item.productName}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{item.qty}</td>
                      <td className="px-3 py-2 text-right">
                        <Input type="number" min="0" max={String(item.qty)} value={String(received)}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleQtyChange(item.productId, e.target.value)}
                          className="w-[80px] text-right ml-auto" />
                      </td>
                      <td className={cn("px-3 py-2 text-right tabular-nums", shortage > 0 && "text-(--danger) font-medium")}>
                        {shortage > 0 ? `-${shortage}` : "0"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t border-(--border-default) bg-(--bg-secondary)">
                  <td className="px-3 py-2 font-medium">Total</td>
                  <td className="px-3 py-2 text-right tabular-nums font-medium">{totalSent}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-medium">{totalReceived}</td>
                  <td className={cn("px-3 py-2 text-right tabular-nums font-medium", totalSent - totalReceived > 0 && "text-(--danger)")}>
                    {totalSent - totalReceived > 0 ? `-${totalSent - totalReceived}` : "0"}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </SheetBody>
      <SheetFooter>
        <Button onClick={handleSubmit}><Check className="size-4" /> Confirm Receipt</Button>
        <Button variant="ghost" onClick={onCancel}>Cancel</Button>
      </SheetFooter>
    </>
  );
}

/* ── Detail field helper ──────────────────────────────────── */

function DetailField({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary)">{label}</div>
      <div className="text-[13px] text-(--text-primary) font-mono tabular-nums">{value}</div>
    </div>
  );
}
